import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { NotificationChannels } from '@fitness-force/enum';
import { AppErrors, NotFoundError, ConflictError } from '@fitness-force/models';;
import type {
  ListForcefullyStatusChangeResponse,
  ResumeBillingRequest,
  StopBillingRequest,
  SubscriptionPlan,
  UpdatePlanStatusRequest,
  voidSubscriptionRequest,
  JsonPatchOperation,
} from '@fitness-force/models';
import { environment } from '@fitness-force/environments';
import { throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { CONST_URL_SEGEMENTS } from '@fitness-force/constants';
@Injectable({
  providedIn: 'root'
})
export class SubscriptionService {
  private http = inject(HttpClient);
  private router = inject(Router);


  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
    }),
  };



  createSubscriptionPlan(
    tenantId: number,
    createSubscriptionPlanRequest: SubscriptionPlan
  ) {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.PLAN.CREATE_SUBSCRIPTION_PLAN
      .replace('{tenant_id}', tenantId.toString());
    return this.http
      .post(
        url,
        createSubscriptionPlanRequest,
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  updateSubscriptionPlan(
    tenantId: number,
    sub_plan_od: number,
    updateSubscriptionPlanRequest: SubscriptionPlan
  ) {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.PLAN.UPDATE_SUBSCRIPTION_PLAN
      .replace('{tenant_id}', tenantId.toString())
      .replace('{plan_id}', sub_plan_od.toString());
    return this.http
      .put(
        url,
        updateSubscriptionPlanRequest,
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  getSingleSubscriptionPlan(tenantId: number, sub_plan_id: number) {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.PLAN.GET_SUBSCRIPTION_PLAN_BY_ID
      .replace('{tenant_id}', tenantId.toString())
      .replace('{plan_id}', sub_plan_id.toString());
    return this.http
      .get(
        url,
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  getSubscriptionPlans(tenantId: number, allParams: Array<string> = []) {
    let parsedParams = new HttpParams()
    if (allParams.length > 0) {

      allParams.forEach((p: any) => {
        parsedParams = parsedParams.append(p.name, p.value);
      });
    }
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.PLAN.GET_SUBSCRIPTION_PLANS
      .replace('{tenant_id}', tenantId.toString());
    return this.http
      .get(
        url,
        { headers: this.httpOptions.headers, params: parsedParams }
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }


  getSubscriptionPlansAll(tenantId: number) {

    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.PLAN.GET_SUBSCRIPTION_PLANS
      .replace('{tenant_id}', tenantId.toString());
    return this.http
      .get(
        url,
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }



  markActive(tenantId: number, request: UpdatePlanStatusRequest) {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.PLAN.MARK_SUBSCRIPTION_PLAN_ACTIVE
      .replace('{tenant_id}', tenantId.toString());
    return this.http.post(url, request, this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  markInActive(tenantId: number, request: UpdatePlanStatusRequest) {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.PLAN.MARK_SUBSCRIPTION_PLAN_INACTIVE
      .replace('{tenant_id}', tenantId.toString());
    return this.http.post(url, request, this.httpOptions).pipe(
      map((res: any) => {
        return res;
      }),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        }
        return throwError(() => new AppErrors(error));
      })
    );
  }

  getSubscriptionPlanClients(tenantId: number, sub_plan_id: number, allParams: Array<string> = []) {

    let parsedParams = new HttpParams()
    if (allParams.length > 0) {

      allParams.forEach((p: any) => {

        parsedParams = parsedParams.append(p.name, p.value);
      });
    }
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.PLAN.GET_SUBSCRIPTION_PLAN_CLIENTS
      .replace('{tenant_id}', tenantId.toString())
      .replace('{plan_id}', sub_plan_id.toString());
    return this.http
      .get(
        url,
        { headers: this.httpOptions.headers, params: parsedParams }
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  resendPaymentLink(tenantId: number, clientId: number, signupUuid: string, channel: any[]) {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.PLAN.RESEND_PAYMENT_LINK
      .replace('{signup_uuid}', signupUuid)
      .replace('{tenant_id}', tenantId.toString())
      .replace('{client_id}', clientId.toString());
    return this.http.post(url, channel
      , this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  VoidSubscription(tenantId: number, clientId: number, subscriptionId: number, voidRequest: voidSubscriptionRequest) {
    return this.http.post(environment.BASE_URL + CONST_URL_SEGEMENTS.SUBSCRIPTION.VOID_SUBSCRIPTION
      .replace('{tenant_id}', tenantId.toString())
      .replace('{client_id}', clientId.toString())
      .replace('{subscription_id}', subscriptionId.toString()), voidRequest, this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  VoidSignUp(tenantId: number, clientId: number, signUpId: number, request: any) {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.PLAN.VOID_SIGNUP
      .replace('{tenant_id}', tenantId.toString())
      .replace('{client_id}', clientId.toString())
      .replace('{signup_id}', signUpId.toString());
    return this.http.post(url, request, this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  VoidUpgradeSubscription(tenantId: number, clientId: number, subscriptionId: number, voidRequest: voidSubscriptionRequest) {
    const url = environment.BASE_URL + CONST_URL_SEGEMENTS.PLAN.VOID_UPGRADE_SUBSCRIPTION
      .replace('{tenant_id}', tenantId.toString())
      .replace('{client_id}', clientId.toString())
      .replace('{subscription_id}', subscriptionId.toString());
    return this.http.post(url, voidRequest, this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  ClientSubscriptionSendManageLink(tenant_id: number, clientId: number, subscriptionId: number, channels: NotificationChannels[]) {
    return this.http.post(environment.BASE_URL + CONST_URL_SEGEMENTS.SUBSCRIPTION.SEND_MANAGE_LINK
      .replace('{tenant_id}', tenant_id.toString())
      .replace('{client_id}', clientId.toString())
      .replace('{subscription_id}', subscriptionId.toString())
      , channels
      , this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  createBulkSubscriptionPlan(data: any) {
    return this.http
      .post(
        `${environment.BASE_URL}/insert_enterprise_master`,
        data,
        this.httpOptions
      )
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          if (error.status === 409) {
            return throwError(() => new ConflictError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  getBulkSubscriptionPlans(allParams: Array<string> = []) {
    let parsedParams = new HttpParams()
    if (allParams.length > 0) {

      allParams.forEach((p: any) => {
        parsedParams = parsedParams.append(p.name, p.value);
      });
    }
    return this.http.get(`${environment.BASE_URL}/list_enterprise_master`, { headers: this.httpOptions.headers, params: parsedParams })
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  getBulkSubscriptionPlanById(subscriptionId: number) {
    return this.http.get(`${environment.BASE_URL}/${subscriptionId}/get_enterprise_master_by_id`, this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => { 
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  updateBulkSubscriptionPlan(subscriptionId: number, data: any) {
    return this.http.put(`${environment.BASE_URL}/${subscriptionId}/update_enterprise_master`, data, this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  addFamilyMemberToSubscription(tenantId: number, clientId: number, subscriptionId: number, familyMemberRequest: any) {
    return this.http.post(`${environment.BASE_URL}/clients/${clientId}/subscription/${subscriptionId}/family_members`, familyMemberRequest, this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  getSubscriptionFamilyMenbers(tenantId: number, subscriptionPlanId: number) {
    return this.http.get(environment.BASE_URL + CONST_URL_SEGEMENTS.SUBSCRIPTION.GET_FAMILY_MEMBERS
      .replace('{tenant_id}', tenantId.toString())
      .replace('{subscription_plan_id}', subscriptionPlanId.toString()), this.httpOptions)
      .pipe(
        map((res: any) => {
          return res;
        }),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        })
      );
  }

  stopBilling(
    clientId: number,
    cltSubsId: number,
    request: StopBillingRequest,
  ) {
    const url =
      `${environment.BASE_URL}${CONST_URL_SEGEMENTS.CLIENT.GET_CLIENTS}/${clientId}` +
      CONST_URL_SEGEMENTS.CLIENT.STOP_BILLING.replace(
        '{clt_subs_id}',
        cltSubsId.toString(),
      );
    return this.http.post(url, request, this.httpOptions).pipe(
      map((res: unknown) => res),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        }
        return throwError(() => new AppErrors(error));
      }),
    );
  }

  resumeBilling(
    clientId: number,
    cltSubsId: number,
    request: ResumeBillingRequest,
  ) {
    const url =
      `${environment.BASE_URL}${CONST_URL_SEGEMENTS.CLIENT.GET_CLIENTS}/${clientId}` +
      CONST_URL_SEGEMENTS.CLIENT.RESUME_BILLING.replace(
        '{clt_subs_id}',
        cltSubsId.toString(),
      );
    return this.http.post(url, request, this.httpOptions).pipe(
      map((res: unknown) => res),
      catchError((error: Response) => {
        if (error.status === 404) {
          return throwError(() => new NotFoundError(error));
        }
        return throwError(() => new AppErrors(error));
      }),
    );
  }

  patchSubscription(
    clientId: number,
    cltSubsId: number,
    patchOperations: JsonPatchOperation[],
  ) {
    const url =
      `${environment.BASE_URL}${CONST_URL_SEGEMENTS.CLIENT.GET_CLIENTS}/${clientId}` +
      CONST_URL_SEGEMENTS.CLIENT.PATCH_SUBSCRIPTION.replace(
        '{clt_subs_id}',
        cltSubsId.toString(),
      );
    return this.http
      .patch(url, patchOperations, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json-patch+json',
        }),
      })
      .pipe(
        map((res: unknown) => res),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        }),
      );
  }

  forceSubscriptionStatus(
    clientId: number,
    cltSubsId: number,
    subscriptionStatus: string,
    statusChangeReason: string,
  ) {
    const patchOperations: JsonPatchOperation[] = [
      { op: 'replace', path: '/SubscriptionStatus', value: subscriptionStatus },
      { op: 'replace', path: '/StatusChangeReason', value: statusChangeReason },
    ];
    return this.patchSubscription(clientId, cltSubsId, patchOperations);
  }

  clearStatusOverride(
    clientId: number,
    cltSubsId: number,
    overrideChangeReason: string,
  ) {
    const patchOperations: JsonPatchOperation[] = [
      { op: 'replace', path: '/IsStatusOverridden', value: false },
      { op: 'replace', path: '/OverrideChangeReason', value: overrideChangeReason },
    ];
    return this.patchSubscription(clientId, cltSubsId, patchOperations);
  }

  getForcefullyStatusChanges(
    tenantId: number,
    clientId: number,
    cltSubsId: number,
  ) {
    const url =
      environment.BASE_URL +
      CONST_URL_SEGEMENTS.SUBSCRIPTION.FORCEFULLY_STATUS_CHANGES.replace(
        '{tenant_id}',
        tenantId.toString(),
      )
        .replace('{client_id}', clientId.toString())
        .replace('{clt_subs_id}', cltSubsId.toString());
    return this.http
      .get<ListForcefullyStatusChangeResponse>(url, this.httpOptions)
      .pipe(
        map((res) => res),
        catchError((error: Response) => {
          if (error.status === 404) {
            return throwError(() => new NotFoundError(error));
          }
          return throwError(() => new AppErrors(error));
        }),
      );
  }
}
